import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { initTokenizer } from '../utils/tokenizer';
import { lookupWord } from '../utils/jisho';
import { loadWordList, saveWordList, addWord } from '../utils/wordlist';
import { savePosition, loadPosition } from '../utils/library';
import WordPopup from '../components/WordPopup';
import WordListPanel from '../components/WordListPanel';
import { toHiragana } from 'wanakana';

const JAPANESE_RE = /[぀-鿿＀-￯]/;
const KANJI_RE = /[一-鿿]/;
const SINGLE_KANJI_RE = /^[一-鿿]$/;

function extractSentence(tokens, idx) {
  const SENT_END = /[。！？\n]/;
  let start = idx;
  while (start > 0 && !SENT_END.test(tokens[start - 1].surface_form)) start--;
  let end = idx;
  while (end < tokens.length - 1 && !SENT_END.test(tokens[end].surface_form)) end++;
  return tokens.slice(start, end + 1).map(t => t.surface_form).join('');
}

// Accept a Jisho result as matching our query only if the returned word
// equals or starts with the query. Single-char queries always pass.
function isJishoMatch(entry, query) {
  if (!entry || entry === 'offline') return false;
  if (query.length === 1) return true;
  const words = entry.japanese?.map(j => j.word).filter(Boolean) || [];
  return words.some(w => w === query || w.startsWith(query)) ||
    entry.slug === query || entry.slug?.startsWith(query);
}

// Only try compound extension when the clicked token is a single kanji —
// that is when kuromoji has split a compound into individual characters.
// Multi-kanji tokens are already correctly recognised by kuromoji.
async function lookupCompound(tokens, idx) {
  const surface = tokens[idx].surface_form;

  if (!SINGLE_KANJI_RE.test(surface)) {
    return { entry: await lookupWord(surface), tokenCount: 1 };
  }

  // Collect adjacent single-kanji tokens (stop at anything longer or non-kanji)
  const surfaces = [surface];
  for (let j = idx + 1; j < Math.min(idx + 4, tokens.length); j++) {
    if (!SINGLE_KANJI_RE.test(tokens[j].surface_form)) break;
    surfaces.push(tokens[j].surface_form);
  }

  if (surfaces.length === 1) {
    return { entry: await lookupWord(surface), tokenCount: 1 };
  }

  // Look up all compound lengths in parallel
  const compounds = surfaces.map((_, i) => surfaces.slice(0, i + 1).join(''));
  const entries = await Promise.all(compounds.map(w => lookupWord(w)));

  // Pick longest where Jisho's result is actually about that compound
  for (let i = entries.length - 1; i >= 0; i--) {
    if (isJishoMatch(entries[i], compounds[i])) {
      return { entry: entries[i], tokenCount: i + 1 };
    }
  }
  return { entry: entries[0], tokenCount: 1 };
}

export default function ReaderPage({ book, onClose }) {
  const [tokens, setTokens] = useState([]);
  const [status, setStatus] = useState('init');
  const [popup, setPopup] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [wordList, setWordList] = useState(() => loadWordList());
  const [showWordList, setShowWordList] = useState(false);
  const [showFurigana, setShowFurigana] = useState(false);
  const scrollSaveTimer = useRef(null);

  const savedSet = useMemo(() => new Set(wordList.map(w => w.word)), [wordList]);

  useEffect(() => { saveWordList(wordList); }, [wordList]);

  const handleSaveWord = useCallback((token, entry) => {
    setWordList(prev => addWord(prev, token, entry, popup?.sentence));
  }, [popup?.sentence]);

  useEffect(() => {
    let alive = true;
    setStatus('init');
    initTokenizer()
      .then(t => {
        if (!alive) return;
        setTokens(t.tokenize(book.text));
        setStatus('ready');
      })
      .catch(() => alive && setStatus('error'));
    return () => { alive = false; };
  }, [book.text]);

  useEffect(() => {
    if (status !== 'ready') return;
    const pos = loadPosition(book.id);
    if (pos > 0) requestAnimationFrame(() => window.scrollTo(0, pos));
  }, [status, book.id]);

  useEffect(() => {
    if (status !== 'ready') return;
    function onScroll() {
      clearTimeout(scrollSaveTimer.current);
      scrollSaveTimer.current = setTimeout(() => savePosition(book.id, window.scrollY), 400);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(scrollSaveTimer.current);
    };
  }, [status, book.id]);

  const handleClick = useCallback(async (token, idx, e) => {
    if (!JAPANESE_RE.test(token.surface_form)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const position = { x: rect.left, y: rect.bottom, viewportWidth: window.innerWidth, viewportHeight: window.innerHeight };
    const sentence = extractSentence(tokens, idx);
    setPopup(null);
    setLookingUp(true);

    const { entry, tokenCount } = await lookupCompound(tokens, idx);

    // For compounds, build a synthetic token with the merged surface form
    const syntheticToken = tokenCount > 1
      ? { ...token, surface_form: tokens.slice(idx, idx + tokenCount).map(t => t.surface_form).join(''), reading: '' }
      : token;

    setLookingUp(false);
    setPopup({ token: syntheticToken, entry, position, sentence, tokenStart: idx, tokenCount });
  }, [tokens]);

  if (status === 'init') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>📖</div>
          <p style={{ color: '#888', fontSize: 15 }}>Loading dictionary…</p>
          <p style={{ color: '#bbb', fontSize: 12, marginTop: 6 }}>First load takes a few seconds</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#c0392b' }}>Failed to load dictionary.</p>
          <button onClick={onClose} style={{ marginTop: 16, padding: '8px 20px', cursor: 'pointer' }}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#faf9f6' }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(250,249,246,0.95)', backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #e8e6e1',
        padding: '12px 28px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 14, padding: '12px 16px 12px 0', margin: '-12px 0 -12px -4px' }}
        >← Back</button>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#333', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {book.title}
        </span>
        {lookingUp && (
          <span style={{ fontSize: 13, color: '#aaa', flexShrink: 0 }}>Looking up…</span>
        )}
        <button
          onClick={() => setShowFurigana(f => !f)}
          title={showFurigana ? 'Hide furigana' : 'Show furigana'}
          style={{
            background: showFurigana ? '#e8f0fb' : 'none',
            border: '1px solid #e8e6e1', borderRadius: 8,
            cursor: 'pointer', color: showFurigana ? '#4a90d9' : '#555',
            fontSize: 13, padding: '10px 12px', flexShrink: 0,
            fontFamily: '"Hiragino Mincho ProN", serif',
          }}
        >
          あ
        </button>
        <button
          onClick={() => setShowWordList(true)}
          style={{ background: 'none', border: '1px solid #e8e6e1', borderRadius: 8, cursor: 'pointer', color: '#555', fontSize: 13, padding: '10px 14px', flexShrink: 0 }}
        >
          Words {wordList.length > 0 && <span style={{ color: '#4a90d9', fontWeight: 700 }}>{wordList.length}</span>}
        </button>
      </div>

      <div style={{
        maxWidth: 680,
        margin: '0 auto',
        padding: '48px 28px 96px',
        fontSize: 20,
        lineHeight: showFurigana ? 3 : 2.2,
        color: '#1a1a1a',
        fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", "HGS明朝E", Georgia, serif',
        wordBreak: 'break-all',
      }}>
        {tokens.map((token, i) => {
          const isJP = JAPANESE_RE.test(token.surface_form);
          const isActive = popup && i >= popup.tokenStart && i < popup.tokenStart + (popup.tokenCount || 1);
          const isSaved = savedSet.has(token.surface_form);
          const reading = token.reading ? toHiragana(token.reading) : '';
          const showRuby = showFurigana && isJP && KANJI_RE.test(token.surface_form) && reading && reading !== token.surface_form;

          return (
            <span
              key={i}
              onClick={isJP ? e => handleClick(token, i, e) : undefined}
              title={isJP && !showFurigana && reading ? reading : undefined}
              style={{
                cursor: isJP ? 'pointer' : 'default',
                background: isActive ? '#fff3cd' : isSaved ? '#e5f5e8' : 'transparent',
                borderRadius: 2,
                padding: '0 1px',
                transition: 'background 0.1s',
                touchAction: isJP ? 'manipulation' : undefined,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {showRuby ? (
                <ruby>
                  {token.surface_form}
                  <rt style={{ fontSize: '0.5em', fontFamily: 'sans-serif', color: '#888', letterSpacing: 0 }}>{reading}</rt>
                </ruby>
              ) : token.surface_form}
            </span>
          );
        })}
      </div>

      {popup && (
        <WordPopup
          token={popup.token}
          entry={popup.entry}
          position={popup.position}
          onClose={() => setPopup(null)}
          onSave={handleSaveWord}
          isSaved={savedSet.has(popup.token.surface_form)}
        />
      )}
      {showWordList && (
        <WordListPanel
          words={wordList}
          onUpdate={setWordList}
          onClose={() => setShowWordList(false)}
        />
      )}
    </div>
  );
}
